import React from 'react';
import {
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  TextField,
  InputAdornment,
  Avatar,
  Snackbar,
  Alert,
  LinearProgress,
} from '@mui/material';
import {
  CleaningServices,
  Add,
  Search,
  MoreVert,
  Edit,
  CheckCircle,
  Build,
  Report,
  Assignment,
} from '@mui/icons-material';
import HousekeepingTaskDialog from '../components/HousekeepingTaskDialog';

const mockTasks = [
  {
    id: 'HK001',
    room: '204',
    type: 'Cleaning',
    assignedTo: 'Maria Garcia',
    priority: 'High',
    status: 'In Progress',
    startTime: '09:00 AM',
    estimatedTime: '45 min',
    notes: 'Guest checked out, deep clean required',
  },
  {
    id: 'HK002',
    room: '305',
    type: 'Maintenance',
    assignedTo: 'John Smith',
    priority: 'Urgent',
    status: 'Pending',
    startTime: '10:00 AM',
    estimatedTime: '2 hours',
    notes: 'AC not working properly',
  },
  {
    id: 'HK003',
    room: '102',
    type: 'Cleaning',
    assignedTo: 'Lisa Brown',
    priority: 'Normal',
    status: 'Completed',
    startTime: '08:00 AM',
    estimatedTime: '30 min',
    notes: 'Standard cleaning',
  },
  {
    id: 'HK004',
    room: '208',
    type: 'Inspection',
    assignedTo: 'Robert Wilson',
    priority: 'Normal',
    status: 'In Progress',
    startTime: '11:00 AM',
    estimatedTime: '20 min',
    notes: 'Post-cleaning inspection',
  },
  {
    id: 'HK005',
    room: '401',
    type: 'Maintenance',
    assignedTo: 'John Smith',
    priority: 'High',
    status: 'Pending',
    startTime: '02:00 PM',
    estimatedTime: '1 hour',
    notes: 'Leaking faucet in bathroom',
  },
  {
    id: 'HK006',
    room: '303',
    type: 'Cleaning',
    assignedTo: 'Maria Garcia',
    priority: 'Normal',
    status: 'Pending',
    startTime: '01:00 PM',
    estimatedTime: '30 min',
    notes: 'Turn-down service',
  },
];

export default function Housekeeping() {
  const [tasks, setTasks] = React.useState(mockTasks);
  const [searchQuery, setSearchQuery] = React.useState('');
  const [selectedTask, setSelectedTask] = React.useState(null);
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [dialogMode, setDialogMode] = React.useState('add');
  const [snackbar, setSnackbar] = React.useState({ open: false, message: '', severity: 'success' });

  const filteredTasks = tasks.filter((task) => {
    const query = searchQuery.toLowerCase();
    return (
      task.room.includes(query) ||
      task.assignedTo.toLowerCase().includes(query) ||
      task.type.toLowerCase().includes(query) ||
      task.id.toLowerCase().includes(query)
    );
  });

  const handleMenuClick = (event, task) => {
    setAnchorEl(event.currentTarget);
    setSelectedTask(task);
  };

  const handleAddTask = () => {
    setSelectedTask(null);
    setDialogMode('add');
    setDialogOpen(true);
  };

  const handleEditTask = () => {
    setDialogMode('edit');
    setDialogOpen(true);
    setAnchorEl(null);
  };

  const handleSaveTask = (taskData) => {
    if (dialogMode === 'add') {
      setTasks([...tasks, taskData]);
      setSnackbar({
        open: true,
        message: `Task ${taskData.id} created successfully!`,
        severity: 'success',
      });
    } else {
      setTasks(tasks.map(t => t.id === taskData.id ? taskData : t));
      setSnackbar({
        open: true,
        message: `Task ${taskData.id} updated successfully!`,
        severity: 'success',
      });
    }
  };

  const handleCompleteTask = () => {
    if (selectedTask) {
      setTasks(tasks.map(t => 
        t.id === selectedTask.id ? { ...t, status: 'Completed' } : t
      ));
      setSnackbar({
        open: true,
        message: `Task ${selectedTask.id} marked as completed!`,
        severity: 'success',
      });
      setAnchorEl(null);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Completed': return 'success';
      case 'In Progress': return 'primary';
      case 'Pending': return 'warning';
      case 'Cancelled': return 'error';
      default: return 'default';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'Urgent': return 'error';
      case 'High': return 'warning';
      case 'Normal': return 'info';
      case 'Low': return 'default';
      default: return 'default';
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'Cleaning': return <CleaningServices />;
      case 'Maintenance': return <Build />;
      case 'Inspection': return <Assignment />;
      default: return <CleaningServices />;
    }
  };

  const stats = [
    { 
      label: 'Total Tasks', 
      value: tasks.length, 
      color: '#1976d2', 
      icon: Assignment,
    },
    { 
      label: 'In Progress', 
      value: tasks.filter(t => t.status === 'In Progress').length, 
      color: '#2e7d32', 
      icon: CleaningServices,
    },
    { 
      label: 'Pending', 
      value: tasks.filter(t => t.status === 'Pending').length, 
      color: '#ed6c02', 
      icon: Report,
    },
    { 
      label: 'Completed Today', 
      value: tasks.filter(t => t.status === 'Completed').length, 
      color: '#9c27b0',
      icon: CheckCircle,
    },
  ];

  const completionRate = tasks.length > 0 
    ? ((tasks.filter(t => t.status === 'Completed').length / tasks.length) * 100).toFixed(0)
    : 0;

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" fontWeight="bold" gutterBottom>
            Housekeeping Management
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage room cleaning, maintenance, and inspection tasks
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<Add />} onClick={handleAddTask}>
          New Task
        </Button>
      </Box>

      <Grid container spacing={3} mb={3}>
        {stats.map((stat) => {
          const IconComponent = stat.icon;
          return (
            <Grid item xs={6} md={3} key={stat.label}>
              <Card sx={{ bgcolor: `${stat.color}10`, borderLeft: `4px solid ${stat.color}` }}>
                <CardContent>
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Box>
                      <Typography variant="body2" color="text.secondary">{stat.label}</Typography>
                      <Typography variant="h4" fontWeight="bold" color={stat.color}>
                        {stat.value}
                      </Typography>
                    </Box>
                    <Avatar sx={{ bgcolor: stat.color, width: 48, height: 48 }}>
                      <IconComponent />
                    </Avatar>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      <Paper sx={{ mb: 3, p: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6" fontWeight="bold">
            Today's Completion Rate
          </Typography>
          <Typography variant="h4" fontWeight="bold" color="primary">
            {completionRate}%
          </Typography>
        </Box>
        <LinearProgress 
          variant="determinate" 
          value={parseInt(completionRate)} 
          sx={{ height: 10, borderRadius: 5 }}
        />
      </Paper>

      <Paper sx={{ mb: 3, p: 2 }}>
        <TextField
          fullWidth
          placeholder="Search by room number, staff name, task type, or ID..."
          size="small"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
          }}
        />
      </Paper>

      <TableContainer component={Paper}>
        <Table>
          <TableHead sx={{ bgcolor: '#f5f5f5' }}>
            <TableRow>
              <TableCell><strong>Task ID</strong></TableCell>
              <TableCell><strong>Room</strong></TableCell>
              <TableCell><strong>Type</strong></TableCell>
              <TableCell><strong>Assigned To</strong></TableCell>
              <TableCell><strong>Priority</strong></TableCell>
              <TableCell><strong>Start Time</strong></TableCell>
              <TableCell><strong>Duration</strong></TableCell>
              <TableCell><strong>Status</strong></TableCell>
              <TableCell><strong>Actions</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredTasks.map((task) => (
              <TableRow key={task.id} hover>
                <TableCell><strong>{task.id}</strong></TableCell>
                <TableCell>
                  <Chip label={task.room} size="small" variant="outlined" color="primary" />
                </TableCell>
                <TableCell>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Avatar sx={{ bgcolor: '#1976d2', width: 32, height: 32 }}>
                      {getTypeIcon(task.type)}
                    </Avatar>
                    <Typography variant="body2">{task.type}</Typography>
                  </Box>
                </TableCell>
                <TableCell>{task.assignedTo}</TableCell>
                <TableCell>
                  <Chip
                    label={task.priority}
                    size="small"
                    color={getPriorityColor(task.priority)}
                  />
                </TableCell>
                <TableCell>{task.startTime}</TableCell>
                <TableCell>{task.estimatedTime}</TableCell>
                <TableCell>
                  <Chip
                    label={task.status}
                    size="small"
                    color={getStatusColor(task.status)}
                  />
                </TableCell>
                <TableCell>
                  <IconButton size="small" onClick={(e) => handleMenuClick(e, task)}>
                    <MoreVert />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
        <MenuItem onClick={handleEditTask}>
          <Edit fontSize="small" sx={{ mr: 1 }} />
          Edit Task
        </MenuItem>
        <MenuItem onClick={handleCompleteTask}>
          <CheckCircle fontSize="small" sx={{ mr: 1 }} />
          Mark Completed
        </MenuItem>
      </Menu>

      <HousekeepingTaskDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        task={selectedTask}
        onSave={handleSaveTask}
        mode={dialogMode}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
